import z from "zod";
import { fingerprintDataSchema } from "../dtos/fingerprint-data";
import { StatusTierSchema } from "../dtos/status-tier";
import { trackingDataSchema } from "../dtos/tracking-data";
import { baseEntitySchema } from "./base";

export enum DealCategory {
  AirlineMiles = "Airline Miles",
  HotelPoints = "Hotel Points",
  CreditCardPoints = "Credit Card Points",
  Disqualified = "Disqualified",
}

// Stage duration tracking schema
export const stageDurationSchema = z.object({
  stage: z.number(),
  enteredAt: z.date(),
  exitedAt: z.date().optional(),
  durationMs: z.number().optional(), // Duration in milliseconds, calculated when exiting stage
});

export type StageDuration = z.infer<typeof stageDurationSchema>;

// Stage history tracking schema
export const stageHistorySchema = z.object({
  currentStage: z.number(),
  currentStageEnteredAt: z.date(),
  stageDurations: z.array(stageDurationSchema).default([]),
  totalTimeInPipeline: z.number().optional(), // Total time from creation to current stage in milliseconds
});

export type StageHistory = z.infer<typeof stageHistorySchema>;

export const dealResubmissionHistoryItemSchema = z.object({
  submissionKey: z.string(),
  submittedAt: z.date(),
  reason: z.string(),
});

export type DealResubmissionHistoryItem = z.infer<
  typeof dealResubmissionHistoryItemSchema
>;

export const dealDataSchema = z.object({
  category: z.nativeEnum(DealCategory),
  name: z.string(),
  customerId: z.string(),
  productId: z.string().optional(),
  productName: z.string(),
  quantity: z.number(),
  /**
   * The miles figure as typed on the form, before the product's maximum was
   * applied. `quantity` is the capped value the deal trades on. Absent on deals
   * created before this was captured, and equal to `quantity` when no cap
   * applied.
   */
  submittedQuantity: z.number().optional(),
  amount: z.number().min(1),
  buyOrSell: z.string(),
  type: z.string(),
  stage: z.number(),
  unitPrice: z.number(),
  businessLostReason: z.string().optional(),
  accountEmail: z.string().optional(),
  accountPassword: z.string().optional(),
  paymentMethod: z.string().optional(),
  paymentDetails: z.string().optional(),
  paypalEmail: z.string().optional(),
  fingerprints: z.array(fingerprintDataSchema),
  statusTier: StatusTierSchema.optional(),
  // Stage duration tracking
  stageHistory: stageHistorySchema,
  enteredPending: z.boolean().default(false),

  sourceTemporaryDealId: z.string().optional(),
  paymentOption: z.preprocess(
    (val) =>
      val === null || val === undefined || val === "" ? undefined : val,
    z
      .enum(["pay_in_advance", "pay_once_booked", "pay_after_booking"])
      .optional(),
  ),
  promoCode: z
    .object({
      id: z.string(),
      code: z.string(),
      discount: z.number(),
      name: z.string(),
    })
    .optional(),
  lastSubmissionAt: z.date().optional(),
  resubmissionCount: z.number().optional(),
  resubmissionHistory: z.array(dealResubmissionHistoryItemSchema).optional(),
  tracking: trackingDataSchema.optional(),
});

export type DealData = z.infer<typeof dealDataSchema>;
export const dealSchema = baseEntitySchema.merge(dealDataSchema);
export type Deal = z.infer<typeof dealSchema>;
